import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Undo2, Trash2, Send, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#000000', '#ffffff'];
const SIZES = [3, 6, 10, 16];

// Lightweight image annotation editor.
// Loads `src` into a canvas, lets the user draw freehand strokes,
// then returns a PNG Blob via `onSave(blob, fileName)`.
//
// Props:
//   src            image URL (must be same-origin or CORS-enabled)
//   fileName       optional original file name (used to derive the edited file name)
//   onSave         async (blob, fileName) => void
//   onClose        () => void
export default function ImageEditor({ src, fileName, onSave, onClose }) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const strokesRef = useRef([]);     // [{ color, size, points: [{x,y}] }]
  const currentRef = useRef(null);
  const drawingRef = useRef(false);
  const [color, setColor] = useState(COLORS[0]);
  const [size, setSize] = useState(SIZES[1]);
  const [, force] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    for (const stroke of strokesRef.current) {
      drawStroke(ctx, stroke);
    }
    if (currentRef.current) drawStroke(ctx, currentRef.current);
  }, []);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imgRef.current = img;
      const canvas = canvasRef.current;
      if (!canvas) return;
      // Cap canvas pixel size so very large images stay performant
      const maxDim = 1600;
      let w = img.naturalWidth || 800;
      let h = img.naturalHeight || 600;
      const scale = Math.min(1, maxDim / Math.max(w, h));
      canvas.width = Math.round(w * scale);
      canvas.height = Math.round(h * scale);
      setLoaded(true);
      redraw();
    };
    img.onerror = () => setError(t('imageEditor.loadError'));
    img.src = src;
    return () => { imgRef.current = null; };
  }, [src, redraw, t]);

  // Lock body scroll + ESC to close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); undo(); }
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  const pointFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);
    return { x, y };
  };

  const onPointerDown = (e) => {
    if (!loaded) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    currentRef.current = { color, size, points: [pointFromEvent(e)] };
    redraw();
  };
  const onPointerMove = (e) => {
    if (!drawingRef.current || !currentRef.current) return;
    currentRef.current.points.push(pointFromEvent(e));
    redraw();
  };
  const onPointerUp = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (currentRef.current && currentRef.current.points.length > 0) {
      strokesRef.current.push(currentRef.current);
    }
    currentRef.current = null;
    force(n => n + 1);
    redraw();
  };

  const undo = () => {
    if (strokesRef.current.length === 0) return;
    strokesRef.current.pop();
    force(n => n + 1);
    redraw();
  };
  const clear = () => {
    if (strokesRef.current.length === 0) return;
    strokesRef.current = [];
    force(n => n + 1);
    redraw();
  };

  const handleSend = async () => {
    if (saving) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSaving(true);
    setError(null);
    try {
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
          'image/png'
        );
      });
      const base = (fileName || 'image').replace(/\.[^.]+$/, '');
      const outName = `${base}-edited.png`;
      await onSave?.(blob, outName);
      onClose?.();
    } catch (err) {
      console.error('[ImageEditor] save error:', err);
      setError(t('imageEditor.saveError'));
      setSaving(false);
    }
  };

  const hasStrokes = strokesRef.current.length > 0;

  const toolbarBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '8px 12px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: 8,
    color: '#fff',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  };
  const toolbarBtnDisabled = { ...toolbarBtn, opacity: 0.4, cursor: 'not-allowed' };

  return ReactDOM.createPortal(
    <div
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.9)',
        backdropFilter: 'blur(4px)',
        zIndex: 10001,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          color: '#fff',
          flexShrink: 0,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600 }}>{t('imageEditor.title')}</div>
        <button
          type="button"
          onClick={onClose}
          title={t('common.close')}
          aria-label={t('common.close')}
          style={{ ...toolbarBtn, padding: 8 }}
        >
          <X size={18} />
        </button>
      </div>

      {/* Canvas area */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          overflow: 'auto',
          minHeight: 0,
        }}
      >
        {!loaded && !error && (
          <div style={{ color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Loader2 size={18} className="animate-spin" />
            {t('common.loading')}
          </div>
        )}
        {error && <div style={{ color: '#fca5a5', fontSize: 14 }}>{error}</div>}
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onPointerLeave={onPointerUp}
          style={{
            display: loaded ? 'block' : 'none',
            maxWidth: '100%',
            maxHeight: '100%',
            background: '#fff',
            borderRadius: 8,
            boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
            touchAction: 'none',
            cursor: 'crosshair',
          }}
        />
      </div>

      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: '12px 16px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          flexShrink: 0,
        }}
      >
        {/* Colors */}
        <div style={{ display: 'flex', gap: 6 }}>
          {COLORS.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              title={c}
              aria-label={`color ${c}`}
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: c,
                border: color === c ? '3px solid #fff' : '1px solid rgba(255,255,255,0.3)',
                boxShadow: color === c ? '0 0 0 2px rgba(59,130,246,0.6)' : 'none',
                cursor: 'pointer',
                padding: 0,
              }}
            />
          ))}
        </div>

        {/* Sizes */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {SIZES.map(s => (
            <button
              key={s}
              type="button"
              onClick={() => setSize(s)}
              title={`${s}px`}
              aria-label={`${s}px`}
              style={{
                width: 32,
                height: 32,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: size === s ? 'rgba(59,130,246,0.4)' : 'rgba(255,255,255,0.08)',
                border: size === s ? '1px solid #3b82f6' : '1px solid rgba(255,255,255,0.18)',
                borderRadius: 8,
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <span style={{ display: 'inline-block', width: s, height: s, borderRadius: '50%', background: color === '#ffffff' ? '#000' : color }} />
            </button>
          ))}
        </div>

        {/* Actions */}
        <button
          type="button"
          onClick={undo}
          disabled={!hasStrokes}
          title={t('imageEditor.undo')}
          style={hasStrokes ? toolbarBtn : toolbarBtnDisabled}
        >
          <Undo2 size={16} /> {t('imageEditor.undo')}
        </button>
        <button
          type="button"
          onClick={clear}
          disabled={!hasStrokes}
          title={t('imageEditor.clear')}
          style={hasStrokes ? toolbarBtn : toolbarBtnDisabled}
        >
          <Trash2 size={16} /> {t('imageEditor.clear')}
        </button>
        <button
          type="button"
          onClick={handleSend}
          disabled={saving || !loaded}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 16px',
            background: saving || !loaded ? 'rgba(34,197,94,0.4)' : '#22c55e',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 8,
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: saving || !loaded ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          {t('imageEditor.send')}
        </button>
      </div>
    </div>,
    document.body
  );
}

function drawStroke(ctx, stroke) {
  const pts = stroke.points;
  if (!pts || pts.length === 0) return;
  ctx.strokeStyle = stroke.color;
  ctx.lineWidth = stroke.size;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  if (pts.length === 1) {
    // Single dot
    ctx.fillStyle = stroke.color;
    ctx.arc(pts[0].x, pts[0].y, stroke.size / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length - 1; i++) {
    const midX = (pts[i].x + pts[i + 1].x) / 2;
    const midY = (pts[i].y + pts[i + 1].y) / 2;
    ctx.quadraticCurveTo(pts[i].x, pts[i].y, midX, midY);
  }
  const last = pts[pts.length - 1];
  ctx.lineTo(last.x, last.y);
  ctx.stroke();
}
